<script setup>
import { ref, watch, onBeforeMount, onBeforeUnmount } from 'vue';
import { router } from '@inertiajs/vue3';
import MenuItems from '@/Data/PublicNavbarMenuItems.js';
import NavLink from './Partials/NavLink.vue';
import NavLoginLink from './Partials/NavLoginLink.vue';
import ResponsiveNavLink from './Partials/ResponsiveNavLink.vue';
import ResponsiveNavLoginLink from './Partials/ResponsiveNavLoginLink.vue';
import LocaleSelect from './Partials/LocaleSelect.vue';

const emit = defineEmits(['scroll']);

const scrollOptions = {
    capture: true,
    passive: true,
};

const isFixed = ref(false);
const showingNavigationDropdown = ref(false);
const lastKnownScrollPosition = ref(0);
const ticking = ref(false);

watch(showingNavigationDropdown, (showing) => {
    const bodyClassList = document.body.classList;

    if (showing) {
        bodyClassList.add('overflow-hidden');
        isFixed.value = true;
        return;
    }

    bodyClassList.remove('overflow-hidden');
    handleWindowScroll();
});

onBeforeMount(() => {
    window.addEventListener('wheel', handleWindowScroll, scrollOptions);
    window.addEventListener('scroll', handleWindowScroll, scrollOptions);
});

onBeforeUnmount(() => {
    window.addEventListener('wheel', handleWindowScroll, scrollOptions);
    window.addEventListener('scroll', handleWindowScroll, scrollOptions);
});

const handleWindowScroll = () => {
    lastKnownScrollPosition.value = window.scrollY;

    if (!ticking.value) {
        window.requestAnimationFrame(() => {
            const position = Math.max(
                window.pageYOffset,
                document.documentElement.scrollTop,
                document.body.scrollTop
            );

            isFixed.value = position > 0;
            ticking.value = false;
        });

        ticking.value = true;
    }
};

const handleNavigate = (item) => {
    showingNavigationDropdown.value = false;

    if (!route().current(item.route)) {
        router.visit(route(item.route));
    }

    if (item.hash) {
        const timeoutId = setTimeout(() => {
            handleScrollToSection(item);
            clearTimeout(timeoutId);
        }, 500);
    }
};

const handleScrollToSection = (item) => {
    const sectionId = item.hash.replace('#', '');
    const section = document.getElementById(sectionId);

    if (section !== undefined) {
        let top = section.offsetTop;

        if (window.innerWidth < 640) {
            top -= 50;
        }

        window.scrollTo({
            top,
            left: 0,
            behavior: 'smooth',
        });
    }
};
</script>

<template>
    <nav
        :class="{
            'fixed bg-white border-[#DEDEE3]': isFixed || showingNavigationDropdown,
            'absolute bg-transparent border-transparent': !isFixed && !showingNavigationDropdown,
            'h-screen': showingNavigationDropdown,
        }"
        class="top-0 left-0 w-full border-b z-20 transition-all px-6"
    >
        <div class="max-w-7xl mx-auto">
            <div
                :class="{
                    'h-20': isFixed,
                    'h-24': !isFixed,
                }"
                class="flex justify-between"
            >
                <div class="flex">
                    <!-- Logo -->
                    <div class="shrink-0 flex items-center">
                        <Link :href="route('home')">
                            <img src="/img/landing/logo.svg" alt="" />
                        </Link>
                    </div>

                    <!-- Navigation Links -->
                    <div class="hidden space-x-8 sm:-my-px sm:ms-10 sm:flex">
                        <template v-for="(item, key) in MenuItems">
                            <NavLink
                                v-if="item.hash !== '#team' || $page.props.team_exists"
                                :key="key"
                                :item="item"
                                :fixed="isFixed"
                                @click.prevent="handleNavigate(item)"
                            />
                        </template>
                    </div>
                </div>

                <div class="hidden sm:flex sm:items-center sm:ms-6">
                    <LocaleSelect/>
                    <NavLoginLink :fixed="isFixed"/>
                </div>

                <!-- Hamburger -->
                <div class="flex items-center sm:hidden">
                    <button
                        class="inline-flex items-center justify-center rounded-md text-[#282825] transition duration-150 ease-in-out"
                        @click="showingNavigationDropdown = ! showingNavigationDropdown"
                    >
                        <svg
                            class="h-8 w-8"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <path
                                :class="{'hidden': showingNavigationDropdown, 'inline-flex': ! showingNavigationDropdown }"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M4 6h16M4 12h16M4 18h16"
                            />
                            <path
                                :class="{'hidden': ! showingNavigationDropdown, 'inline-flex': showingNavigationDropdown }"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Responsive Navigation Menu -->
        <div :class="{'block': showingNavigationDropdown, 'hidden': ! showingNavigationDropdown}" class="sm:hidden mt-12">
            <div class="flex flex-col gap-8 mt-8">
                <template v-for="(item, key) in MenuItems">
                    <ResponsiveNavLink
                        v-if="item.hash !== '#team' || $page.props.team_exists"
                        :key="key"
                        :item="item"
                        @click.prevent="handleNavigate(item)"
                    />
                </template>

                <ResponsiveNavLoginLink/>
            </div>
        </div>
    </nav>
</template>
